import type { RequestPayload } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Divider, Fieldset, Group, Input, List, MultiSelect, Slider, Stack, Textarea, Title } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { convertToTimeString, convertToUTCTimeString } from "../../helpers/dates";
import { getFullNameWithGroup } from "../../helpers/stringHelper";
import type { Apprentice } from "../../models/apprentice";
import { getCompetenceCategoryOptions } from "../../models/enums/competenceCategory";
import { getImpressionMarks, Impression } from "../../models/enums/impression";
import type { Observation } from "../../models/observation";
import { SaveButton } from "../button/SaveButton";
import { ChipMultiSelect, type ChipMultiSelectItem } from "../chipMultiSelect/ChipMultiSelect";
import { InfoCardPopover } from "../infoCardPopover/InfoCardPopover";

type ObservationFormProps = {
  saveButtonLabel: string;
  onSubmit: (values: RequestPayload) => void;
} & (
  | {
      apprentices: Apprentice[];
      observation?: never;
    }
  | {
      apprentices?: never;
      observation: Observation;
    }
);

type ObservationFormType = {
  apprentices: string[];
  observed_at: string;
  competence_categories: string[];
  impression: Impression;
  text: string;
};

type FormErrors = {
  apprentice_ids?: string[];
  text?: string[];
  observed_at?: string[];
  competence_categories?: string[];
  impression?: string[];
};

export function ObservationForm({ apprentices, onSubmit, observation, saveButtonLabel }: ObservationFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const form = useForm<ObservationFormType>({
    mode: "controlled",
    initialValues: {
      apprentices: observation ? [observation.apprentice.id.toString()] : [],
      observed_at: convertToTimeString(observation?.observed_at) ?? new Date().toISOString(),
      competence_categories: observation?.competence_categories ?? [],
      impression: observation?.impression ?? Impression.NEUTRAL,
      text: observation?.text ?? "",
    },
  });
  const mappedApprentices: ChipMultiSelectItem[] =
    apprentices?.map((a) => ({
      value: a.id.toString(),
      label: getFullNameWithGroup(a),
    })) ?? [];

  const competenceLabel = (
    <Group align="center" gap="xs">
      Kompetenzen
      <InfoCardPopover>
        <Group>
          <Stack>
            <Title order={5}>Fachkompetenz</Title>
            <List icon="-">
              <List.Item>Ausbildungsstand</List.Item>
              <List.Item>Arbeitsqualität</List.Item>
              <List.Item>Arbeitsmenge, Arbeitstempo</List.Item>
              <List.Item>Umsetzung der Berufskenntnisse</List.Item>
            </List>
          </Stack>
          <Stack>
            <Title order={5}>Methodenkompetenz</Title>
            <List icon="-">
              <List.Item>Arbeitstechnik</List.Item>
              <List.Item>Vernetztes Denken und Handeln</List.Item>
              <List.Item>Umgang mit Mitteln und Betriebseinrichtung</List.Item>
              <List.Item>Lern- und Arbeitsstrategie</List.Item>
            </List>
          </Stack>
          <Stack>
            <Title order={5}>Sozialkompetenz</Title>
            <List icon="-">
              <List.Item>Teamfähigkeit, Konfliktfähigkeit</List.Item>
              <List.Item>Zusammenarbeit</List.Item>
              <List.Item>Information und Kommunikation</List.Item>
              <List.Item>Kundenorientiertes Handeln</List.Item>
            </List>
          </Stack>
          <Stack>
            <Title order={5}>Selbstkompetenz</Title>
            <List icon="-">
              <List.Item>Selbständigkeit, Eigenverantwortliches Handeln</List.Item>
              <List.Item>Zuverlässigkeit, Belastbarkeit</List.Item>
              <List.Item>Umgangsformen</List.Item>
              <List.Item>Motivation</List.Item>
            </List>
          </Stack>
        </Group>
      </InfoCardPopover>
    </Group>
  );

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit({ ...values, observed_at: convertToUTCTimeString(values.observed_at) }))}>
      <Fieldset legend="Details">
        <Stack maw="60rem">
          {apprentices && (
            <ChipMultiSelect
              disabled={!!observation}
              items={mappedApprentices}
              inputLabel="Lernende"
              key={form.key("apprentices")}
              {...form.getInputProps("apprentices")}
              error={errors?.apprentice_ids}
            />
          )}
          <MultiSelect
            label={competenceLabel}
            placeholder="Wähle Kompetenzen"
            clearable
            data={getCompetenceCategoryOptions()}
            key={form.key("competence_categories")}
            {...form.getInputProps("competence_categories")}
            error={errors?.competence_categories}
          />
          <DateTimePicker
            label="Zeit"
            valueFormat="DD.MM.YYYY HH:mm"
            withWeekNumbers
            maxDate={observation?.observed_at ?? new Date().toISOString()}
            key={form.key("observed_at")}
            {...form.getInputProps("observed_at")}
            error={errors?.observed_at}
          />
          <Input.Wrapper label="Eindruck" mb="lg">
            <Slider
              label={null}
              max={2}
              min={0}
              step={1}
              marks={getImpressionMarks()}
              key={form.key("impression")}
              {...form.getInputProps("impression")}
              size="lg"
              thumbSize={20}
              styles={{ thumb: { borderWidth: 2, padding: 3 } }}
            />
          </Input.Wrapper>
          {errors?.impression && <Input.Error>{errors.impression}</Input.Error>}
          <Textarea label="Beschreibung" minRows={3} key={form.key("text")} {...form.getInputProps("text")} error={errors?.text} />
          <Divider />
          <SaveButton label={saveButtonLabel} />
        </Stack>
      </Fieldset>
    </form>
  );
}
